/**
 * Import preflight (audit P1): measure what a model is about to cost before the editor commits
 * to it, and say so.
 *
 * An import holds the whole file as an `ArrayBuffer`, builds a scene from it, AND stores a `Blob`
 * copy in IndexedDB — and for an unrigged model the auto-skin voxelization runs on the main
 * thread afterwards. On a mid-range machine a large GLB can therefore turn into an out-of-memory
 * crash or a quota failure with nothing in the UI explaining why.
 *
 * By deliberate decision this NEVER blocks: the user is told what is big and what the browser has
 * room for, and then the import proceeds. Every threshold below is an advisory line, not a limit.
 */

import type { BufferGeometry, Mesh } from 'three';
import type { ImportedModel } from './glbImport';

/** File size past which the in-memory buffer + scene + stored blob is worth mentioning. */
export const LARGE_FILE_BYTES = 50 * 1024 * 1024;
/** Vertex count past which viewport interaction and auto-skin get noticeably slow. */
export const LARGE_VERTEX_COUNT = 300_000;
/** Triangle count past which the same is true of rendering. */
export const LARGE_TRIANGLE_COUNT = 500_000;
/**
 * Vertex count past which the (main-thread) voxel-geodesic auto-skin pass is a multi-second UI
 * freeze rather than a blink. Only relevant for unrigged models, which are the ones that skin.
 */
export const HEAVY_AUTOSKIN_VERTEX_COUNT = 150_000;
/** Storage headroom below which writing this model may fail outright. */
export const MIN_STORAGE_HEADROOM_FACTOR = 2;

export interface StorageEstimate {
  usage: number;
  quota: number;
  /** `quota - usage`, floored at 0. */
  headroomBytes: number;
}

export interface ImportPreflight {
  fileBytes: number;
  vertexCount: number;
  triangleCount: number;
  /** `undefined` when the browser does not implement `navigator.storage.estimate()`. */
  storage?: StorageEstimate;
  /** Human-readable advisories. Never an error — the import runs regardless. */
  warnings: string[];
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** Vertices and triangles over every mesh of the model, indexed or not. */
export function countGeometry(model: ImportedModel): {
  vertexCount: number;
  triangleCount: number;
} {
  let vertexCount = 0;
  let triangleCount = 0;

  const meshes: Mesh[] = [...model.meshes, ...model.skinnedMeshes];
  for (const mesh of meshes) {
    const geometry: BufferGeometry | undefined = mesh.geometry;
    const position = geometry?.getAttribute('position');
    if (!position) continue;
    vertexCount += position.count;
    triangleCount += Math.floor((geometry.index ? geometry.index.count : position.count) / 3);
  }

  return { vertexCount, triangleCount };
}

/**
 * Ask the browser how much room is left. Returns `undefined` when the API is missing (Safari
 * before 17, node) — an unknown quota is not a reason to complain at the user.
 */
export async function readStorageEstimate(): Promise<StorageEstimate | undefined> {
  const storage: StorageManager | undefined =
    typeof navigator !== 'undefined' ? navigator.storage : undefined;
  if (!storage || typeof storage.estimate !== 'function') return undefined;
  try {
    const estimate = await storage.estimate();
    const usage = typeof estimate.usage === 'number' ? estimate.usage : 0;
    const quota = typeof estimate.quota === 'number' ? estimate.quota : 0;
    if (quota <= 0) return undefined;
    return { usage, quota, headroomBytes: Math.max(0, quota - usage) };
  } catch {
    // `estimate()` throws in some private-browsing modes; that is not an import problem.
    return undefined;
  }
}

/**
 * Measure a model that has already parsed successfully and collect the advisories worth showing.
 * Called after the parse (so the counts are real) and before anything is written (so a quota
 * warning arrives while it can still mean something).
 */
export async function preflightImport(
  buffer: ArrayBuffer,
  model: ImportedModel,
): Promise<ImportPreflight> {
  const fileBytes = buffer.byteLength;
  const { vertexCount, triangleCount } = countGeometry(model);
  const storage = await readStorageEstimate();
  const warnings: string[] = [];

  if (fileBytes >= LARGE_FILE_BYTES) {
    warnings.push(
      `This model is ${formatBytes(fileBytes)}. It is held in memory and stored locally at ` +
        'full size, so expect slow imports and heavy memory use.',
    );
  }

  if (vertexCount >= LARGE_VERTEX_COUNT || triangleCount >= LARGE_TRIANGLE_COUNT) {
    warnings.push(
      `This model has ${formatCount(vertexCount)} vertices and ` +
        `${formatCount(triangleCount)} triangles; viewport interaction may be slow.`,
    );
  }

  if (model.kind === 'unrigged' && vertexCount >= HEAVY_AUTOSKIN_VERTEX_COUNT) {
    warnings.push(
      `Auto-skinning ${formatCount(vertexCount)} vertices runs on the main thread and will ` +
        'freeze the interface for several seconds. Lower the skin resolution if it is too slow.',
    );
  }

  if (storage) {
    const needed = fileBytes * MIN_STORAGE_HEADROOM_FACTOR;
    if (storage.headroomBytes < needed) {
      warnings.push(
        `Local storage has about ${formatBytes(storage.headroomBytes)} free but this model needs ` +
          `roughly ${formatBytes(needed)} to store safely. Saving may fail — delete unused ` +
          'projects, or free up disk space.',
      );
    }
  }

  return { fileBytes, vertexCount, triangleCount, storage, warnings };
}
